import type { Rng, RoundOutcome } from '../../core/contracts'

/**
 * Memory Matrix - зрительно-пространственная память.
 * Основа методики: Corsi block-tapping test.
 *
 * Игроку показывают подсвеченный паттерн на сетке, затем гасят.
 * Задача - воспроизвести, какие клетки горели. Порядок не важен,
 * важен набор: меряем объём, а не последовательность.
 *
 * Здесь только чистые функции. Ни React, ни таймеров, ни Math.random.
 */

export interface MatrixRound {
  /** Сторона сетки: 3 = 3x3 */
  readonly gridSize: number
  /** Индексы подсвеченных клеток, отсортированы */
  readonly pattern: readonly number[]
  /** Сколько миллисекунд показывать паттерн */
  readonly showMs: number
}

export interface MatrixAnswer {
  /** Индексы клеток, которые выбрал игрок */
  readonly cells: readonly number[]
  /** Время от момента, когда паттерн погас, до подтверждения */
  readonly rtMs: number
}

export interface LevelSpec {
  readonly gridSize: number
  readonly cells: number
  /** Сколько миллисекунд показа приходится на одну клетку паттерна */
  readonly msPerCell: number
}

/**
 * Таблица уровней. Растёт по трём осям сразу:
 * плотность паттерна, размер сетки и СКОРОСТЬ показа.
 *
 * Время на клетку падает с уровнем: новичку дают рассмотреть,
 * опытному - только взгляд. Отдельной настройки скорости нет
 * намеренно: уровень и так двигается от точности игрока, поэтому
 * скорость адаптируется тем же механизмом. Лишний переключатель
 * означал бы второй источник правды о сложности.
 */
const LEVELS: readonly LevelSpec[] = [
  { gridSize: 3, cells: 3, msPerCell: 400 },
  { gridSize: 3, cells: 4, msPerCell: 370 },
  { gridSize: 4, cells: 5, msPerCell: 340 },
  { gridSize: 4, cells: 6, msPerCell: 320 },
  { gridSize: 5, cells: 6, msPerCell: 300 },
  { gridSize: 5, cells: 7, msPerCell: 285 },
  { gridSize: 5, cells: 8, msPerCell: 270 },
  { gridSize: 5, cells: 9, msPerCell: 255 },
  { gridSize: 6, cells: 10, msPerCell: 245 },
  { gridSize: 6, cells: 11, msPerCell: 235 },
  { gridSize: 6, cells: 12, msPerCell: 215 },
  { gridSize: 6, cells: 13, msPerCell: 200 },
]

export const MIN_LEVEL = 1
export const MAX_LEVEL = LEVELS.length
export const INITIAL_LEVEL = 4

/** Рамки времени показа: ниже - не успеть увидеть, выше - скучно ждать. */
export const MIN_SHOW_MS = 900
export const MAX_SHOW_MS = 3400

/** Эталонное время вспоминания в этой игре. */
export const REFERENCE_RT_MS = 3000

export function specForLevel(level: number): LevelSpec {
  const index = Math.min(Math.max(level, MIN_LEVEL), MAX_LEVEL) - 1
  const spec = LEVELS[index]
  if (!spec) throw new Error(`Memory Matrix: нет спецификации для уровня ${level}`)
  return spec
}

/**
 * @param pace личный темп игрока: показ растягивается под тех,
 *   кто обычно отвечает медленнее эталона.
 */
export function generateRound(level: number, rng: Rng, pace = 1): MatrixRound {
  const { gridSize, cells, msPerCell } = specForLevel(level)
  const total = gridSize * gridSize

  const pattern = rng
    .shuffle(Array.from({ length: total }, (_, i) => i))
    .slice(0, cells)
    .sort((a, b) => a - b)

  const showMs = Math.round(
    Math.min(Math.max(cells * msPerCell * pace, MIN_SHOW_MS), MAX_SHOW_MS),
  )

  return { gridSize, pattern, showMs }
}

/**
 * Оценка ответа.
 *
 * accuracy = (попадания - промахи) / размер паттерна, не ниже нуля.
 * Штраф за лишние клетки обязателен: иначе выгодно тапнуть всё подряд
 * и получить идеальную точность.
 *
 * Считаем именно долю, а не «верно / неверно»: адаптивной сложности
 * нужна градация, чтобы понимать, насколько игрок близок к пределу.
 */
export function evaluate(round: MatrixRound, answer: MatrixAnswer): RoundOutcome {
  const target = new Set(round.pattern)
  const picked = new Set(answer.cells)

  let hits = 0
  for (const cell of picked) if (target.has(cell)) hits++

  const falsePositives = picked.size - hits
  const size = round.pattern.length

  const accuracy = size === 0 ? 0 : Math.max(0, (hits - falsePositives) / size)

  return {
    correct: hits === size && falsePositives === 0,
    accuracy,
    rtMs: answer.rtMs,
  }
}

/** Разбор ответа для подсветки: что угадано, что пропущено, где промах. */
export function describeAnswer(
  round: MatrixRound,
  answer: MatrixAnswer,
): { hit: number[]; missed: number[]; wrong: number[] } {
  const target = new Set(round.pattern)
  const picked = new Set(answer.cells)

  return {
    hit: [...picked].filter((c) => target.has(c)).sort((a, b) => a - b),
    missed: [...target].filter((c) => !picked.has(c)).sort((a, b) => a - b),
    wrong: [...picked].filter((c) => !target.has(c)).sort((a, b) => a - b),
  }
}
